import { Logo } from "@/components/branding/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { clientAuthService } from "@/services/clientAuth.service/clientAuth.api";
import { authService } from "@/services/auth.service/auth.api";

import { Building, EyeIcon, EyeOffIcon, Lock, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

export default function Login() {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    subdomain: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();


  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.password || !formData.subdomain) {
      toast({
        title: "Erro",
        description: "Por favor, preencha todos os campos",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    const [data] = await authService.login(
      {
        email: formData.email,
        password: formData.password,
        subdomain: formData.subdomain,
      },
      { ignoreError: true }
    );

    if (data?.accessToken) {
      setIsLoading(false);
      toast({ title: "Sucesso", description: "Login realizado com sucesso!" });
      navigate("/dashboard");
      return;
    }

    const [clientData] = await clientAuthService.login(
      {
        identifier: formData.email,
        password: formData.password,
        subdomain: formData.subdomain.trim(),
        tenant: formData.subdomain.trim(),
      },
      { ignoreError: true }
    );
    setIsLoading(false);

    if (clientData?.accessToken) {
      const sub = formData.subdomain.trim();
      toast({ title: "Sucesso", description: "Bem-vindo ao portal do cliente!" });
      navigate(`/${encodeURIComponent(sub)}/cliente/tickets`);
      return;
    }

    toast({
      title: "Erro",
      description: "E-mail ou senha inválidos",
      variant: "destructive",
    });
  }

  useEffect(() => {
    const user = authService.getCurrentUser();
    if (user) {
      navigate('/dashboard');
    }


  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="bg-card shadow-lg rounded-lg p-8 w-full max-w-md animate-fadeIn">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Logo size={32} textClassName="text-2xl" />
          </div>
          <h1 className="text-2xl font-bold">Bem-vindo de volta</h1>
          <p className="text-muted-foreground">Entre para acessar sua conta</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="subdomain" className="block text-sm font-medium">
                Subdomínio
              </label>
              <div className="relative">
                <Building className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="subdomain"
                  type="text"
                  placeholder="sua-empresa"
                  className="pl-10"
                  value={formData.subdomain}
                  onChange={(e) => handleChange("subdomain", e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  className="pl-10"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium">
                  Senha
                </label>
                <Link to="/esqueci-senha" className="text-sm text-primary hover:underline">
                  Esqueceu a senha?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className="pl-10"
                  value={formData.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1 h-8 w-8 p-0"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ?
                    <EyeOffIcon className="h-4 w-4" /> :
                    <EyeIcon className="h-4 w-4" />
                  }
                </Button>
              </div>
            </div>

            <Button type="submit" className="w-full" loading={isLoading}>
              Entrar
            </Button>
          </div>
        </form>

        <div className="mt-6 text-center text-sm text-muted-foreground">
          Não tem uma conta? <Link to="/register" className="text-primary hover:underline">Cadastre-se</Link>
        </div>

        <div className="mt-6 pt-6 border-t text-center">
          <p className="text-sm text-muted-foreground mb-2">É contato ou cliente?</p>
          <Link
            to={
              formData.subdomain.trim()
                ? `/${formData.subdomain.trim()}/cliente/login`
                : "/cliente/login"
            }
            className="text-sm font-medium text-primary hover:underline"
          >
            Entre aqui para abrir e ver seus tickets
          </Link>
        </div>
      </div>
    </div>
  );
}
